import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { unsubscribe } from '../../../decorators/unsubscribe.decorator';
import { ToasterService } from '../../../services/toaster.service';
import { UtilityService } from '../../../services/utility.service';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { SeoService } from '../../../services/seo.service';
import { pageSeoInfo } from '../../../constants/page-seo-info';
import { RadioInputComponent } from '../../../components/utility/form/radio-input/radio-input.component';
import { CheckboxInputComponent } from '../../../components/utility/form/checkbox-input/checkbox-input.component';
import { routes } from '../../../constants/routes';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogActionComponent } from '../../../components/utility/confirmation-dialog-action/confirmation-dialog-action.component';
import { MergeSuccessDialogComponent } from '../../../components/utility/merge-success-dialog/merge-success-dialog.component';

@Component({
  selector: 'app-multiple-phone-sign-up',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RadioInputComponent,
    CheckboxInputComponent,
    RouterLink,
  ],
  templateUrl: './multiple-phone-sign-up.component.html',
  styleUrl: './multiple-phone-sign-up.component.scss',
})
@unsubscribe()
export class MultiplePhoneSignUpComponent implements OnInit, OnDestroy {
  public phoneSelectionForm!: FormGroup;
  public userDetails: any;
  public multiplePhones: {
    phone: string;
    provider: string;
    userId: string | null;
  }[] = [];
  isPhoneConfirmed: boolean = false;
  selectedPhone: {
    phone: string;
    provider: string;
    userId: string | null;
  } | null = null;
  routes = routes;
  encodedProfile: string | null = null;

  constructor(
    private router: Router,
    public toaster: ToasterService,
    private apiService: ApiService,
    private authService: AuthService,
    public utilityService: UtilityService,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
    private matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.initPhoneSelectionForm();
    this.encodedProfile =
      this.activatedRoute.snapshot.queryParams['profileDetails'];
    if (this.encodedProfile) {
      try {
        const decoded = decodeURIComponent(atob(this.encodedProfile));
        this.userDetails = JSON.parse(decoded);
        this.multiplePhones = this.userDetails?.multiplePhone || [];
      } catch {
        this.toaster.error('Failed to load profile details');
      }
    }
    const { title, description, canonicalUrl } = pageSeoInfo['profile'];
    this.seoService.setPageTitle(title);
    this.seoService.setPageDescription(description);
    this.seoService.setCanonicalUrl(canonicalUrl);
  }

  private initPhoneSelectionForm(): void {
    this.phoneSelectionForm = new FormGroup({
      selectedPhone: new FormControl('', [Validators.required]),
      isConfirmed: new FormControl(false, [Validators.requiredTrue]),
    });
  }

  onPhoneSelection(phone: {
    phone: string;
    provider: string;
    userId: string | null;
  }): void {
    this.selectedPhone = phone;
    this.isPhoneConfirmed = false;
    this.phoneSelectionForm.patchValue({
      selectedPhone: phone.phone,
    });
  }

  onConfirmSelection(): void {
    if (!this.phoneSelectionForm.get('isConfirmed')?.value) {
      this.toaster.error('Please confirm the selection');
      return;
    }

    if (!this.selectedPhone) {
      this.toaster.error('Please select a phone number');
      return;
    }

    this.matDialog
      .open(ConfirmationDialogActionComponent, {
        data: {
          dialogIcon: 'images/phone-icon.svg',
          iconAlt: 'Phone',
          iconTitle: 'Confirm Your Choice',
          headerText: 'Confirm Your Choice',
          message: `<strong>${this.selectedPhone.phone}</strong> from your ${this.selectedPhone.provider} account will be your login phone number going forward. You can update this later in your profile settings.`,
          viewAsinnerHTML: true,
          primaryButtonText: 'Yes, Confirm',
          secondaryButtonText: 'Go Back',
          primaryButtonClass: 'btn-secondary',
          secondaryButtonClass: 'btn-bordered-black',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.updateMobileNumber();
        }
      });
  }

  updateMobileNumber() {
    this.apiService
      .mergeProfileByPhone({
        id: this.userDetails?.id,
        parichayId: this.userDetails?.janParichayId,
        phone: this.selectedPhone.phone,
      })
      .subscribe({
        next: (response) => {
          if (response?.token) {
            this.matDialog
              .open(MergeSuccessDialogComponent, {
                data: {
                  name: response?.name || this.userDetails?.name || '',
                  email: response?.email || this.userDetails?.email || '',
                  phone: this.selectedPhone?.phone || response?.phone || '',
                  provider: 'Jan Parichay',
                },
                disableClose: true,
                panelClass: 'merge-success-panel',
              })
              .afterClosed()
              .subscribe(() => {
                const userData = {
                  email: response?.email || this.userDetails?.email || '',
                  phone: this.selectedPhone?.phone || response?.phone || '',
                };
                const queryParams = this.userDetails?.isLoggedInUser
                  ? {
                      isDetailsVerifiedAndMatch: 'true',
                      data: btoa(encodeURIComponent(JSON.stringify(userData))),
                    }
                  : {};
                this.authService.saveUserProfile(response);
                this.authService.redirectToDashboard(queryParams);
              });
          } else {
            const encodedData = btoa(
              encodeURIComponent(JSON.stringify(response)),
            );
            this.router.navigate(
              ['/', routes.account, routes.signUp, routes.myProfile],
              {
                replaceUrl: true,
                queryParams: {
                  data: encodedData,
                  success: 'y',
                  isMultiplePhone: true,
                },
              },
            );
          }
        },
        error: (error) => {
          const errorMsg =
            error instanceof Error ? error.message : 'Something went wrong';
          this.toaster.error(errorMsg);
        },
      });
  }

  onGoBack(): void {
    this.router.navigate(['/register']);
  }

  onSkipSelection(): void {
    this.authService.redirectToDashboard();
  }

  get selectedPhoneControl() {
    return this.phoneSelectionForm.get('selectedPhone') as FormControl;
  }

  get isConfirmedControl() {
    return this.phoneSelectionForm.get('isConfirmed') as FormControl;
  }

  ngOnDestroy(): void {}
}
